import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import SimpleMap from "@/components/LeafletMap";
import { MapPin, CheckCircle2, Clock } from "lucide-react";

export default function ParentTracking() {
  const { missionId } = useParams();
  const navigate = useNavigate();
  const [missions, setMissions] = useState([]);
  const [mission, setMission] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [events, setEvents] = useState([]);

  const currentId = missionId;

  const loadList = useCallback(async () => {
    const { data } = await api.get("/missions");
    setMissions(data);
    if (!currentId && data.length) {
      const active = data.find((m) => m.status === "active") || data[0];
      navigate(`/parent/tracking/${active.id}`, { replace: true });
    }
  }, [currentId, navigate]);

  const loadMission = useCallback(async () => {
    if (!currentId) return;
    try {
      const { data } = await api.get(`/missions/${currentId}`);
      setMission(data);
      const ev = await api.get(`/missions/${currentId}/events`);
      setEvents(ev.data);
    } catch { /* ignore */ }
  }, [currentId]);

  const loadTracking = useCallback(async () => {
    if (!currentId) return;
    try { const { data } = await api.get(`/missions/${currentId}/tracking`); setTracking(data); }
    catch { /* ignore */ }
  }, [currentId]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadMission(); }, [loadMission]);
  useEffect(() => {
    loadTracking();
    const t = setInterval(loadTracking, 3000);
    return () => clearInterval(t);
  }, [loadTracking]);

  const waypoints = mission?.route
    ? [{ lat: mission.route.school_lat, lng: mission.route.school_lng }, ...mission.route.stops.map((s) => ({ lat: s.lat, lng: s.lng }))]
    : [];

  return (
    <div className="p-4 pb-6" data-testid="parent-tracking-page">
      <div className="mb-4">
        <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Live</div>
        <h1 className="font-display font-black text-3xl tracking-tighter">Suivi</h1>
      </div>

      {missions.length > 1 && (
        <select data-testid="parent-mission-select" value={currentId || ""} onChange={(e) => navigate(`/parent/tracking/${e.target.value}`)}
          className="mb-3 w-full border border-zinc-900 px-3 py-2 bg-white">
          {missions.map((m) => <option key={m.id} value={m.id}>{m.route?.name} — {m.status}</option>)}
        </select>
      )}

      {!mission ? (
        <div className="border border-zinc-900 p-6 text-sm text-zinc-500 text-center">Aucun trajet actif.</div>
      ) : (
        <>
          <SimpleMap waypoints={waypoints} position={tracking?.position} height={280} testId="parent-tracking-map" />

          {tracking && mission.status === "active" && (
            <div className="mt-3 grid grid-cols-3 gap-0 border border-zinc-900 bg-white" data-testid="parent-tracking-stats">
              <div className="p-3 text-center border-r border-zinc-900">
                <div className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">Vitesse</div>
                <div className="font-display font-black text-2xl tracking-tighter">{tracking.speed_kmh}<span className="text-xs text-zinc-500"> km/h</span></div>
              </div>
              <div className="p-3 text-center border-r border-zinc-900">
                <div className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">ETA</div>
                <div className="font-display font-black text-2xl tracking-tighter">{tracking.eta_minutes}<span className="text-xs text-zinc-500"> min</span></div>
              </div>
              <div className={`p-3 text-center ${tracking.delay_minutes > 0 ? "bg-red-500 text-white" : "bg-[#FFC60A]"}`}>
                <div className="text-[10px] font-mono tracking-widest uppercase opacity-80">Retard</div>
                <div className="font-display font-black text-2xl tracking-tighter">{tracking.delay_minutes}<span className="text-xs"> min</span></div>
              </div>
            </div>
          )}

          <div className="mt-4 border border-zinc-900 p-4 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Mission</div>
                <div className="font-display font-bold text-lg mt-0.5">{mission.route?.name}</div>
              </div>
              <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 ${mission.status === "active" ? "bg-[#FFC60A]" : mission.status === "completed" ? "bg-zinc-900 text-white" : "bg-white"}`}>
                {mission.status}
              </span>
            </div>
            <div className="mt-2 text-xs text-zinc-600 font-mono">
              {mission.vehicle?.plate} · {mission.vehicle?.model} · capacité {mission.vehicle?.capacity}
            </div>
          </div>

          <div className="mt-4 border border-zinc-900 bg-white">
            <div className="p-3 bg-zinc-900 text-white text-[10px] font-mono tracking-widest uppercase">Timeline</div>
            <ol className="divide-y divide-zinc-200">
              {mission.route?.stops?.map((s, i) => {
                const eventsForStop = events.filter((e) => e.stop_id === s.id);
                const done = i < mission.current_stop_index;
                const active = i === mission.current_stop_index && mission.status === "active";
                return (
                  <li key={s.id} className="p-3 flex items-start gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center border border-zinc-900 font-mono font-bold ${done ? "bg-zinc-900 text-[#FFC60A]" : active ? "bg-[#FFC60A]" : "bg-white"}`}>
                      {done ? <CheckCircle2 size={14} /> : i + 1}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold flex items-center gap-1"><MapPin size={12} /> {s.address}</div>
                      {s.scheduled_time && <div className="text-xs text-zinc-500 font-mono flex items-center gap-1"><Clock size={11} /> {s.scheduled_time}</div>}
                      {eventsForStop.map((ev) => {
                        const child = mission.children.find((c) => c.id === ev.child_id);
                        return (
                          <div key={ev.id} className="text-xs text-zinc-700 mt-1">
                            {child?.first_name} · {ev.event_type === "boarding" ? "Monté" : "Descendu"} <span className="font-mono text-zinc-500">{ev.scanned_at.slice(11, 16)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </>
      )}
    </div>
  );
}
